/**
 * Moderator tools and report review.
 */
export const moderation = {
    nav: {
      link: 'Moderation',
      submenuLabel: 'Moderation',
      reports: 'Platform Reports',
      tickets: 'Support Tickets',
      moderationLink: 'Moderation',
      adminLink: 'Platform Admin',
    },
    reports: {
      title: 'Platform Reports',
      subtitle: 'Review and act on content and abuse reports.',
      loadError: 'Failed to load reports.',
      empty: 'No reports match the current filters.',
      refresh: 'Refresh',
      filterStatus: 'Status',
      filterAssigned: 'Assignment',
      statusAll: 'All',
      statusOpenEscalated: 'Open + Escalated',
      status: {
        open: 'Open',
        escalated: 'Escalated',
        resolved: 'Resolved',
        closed: 'Closed',
      },
      assigned: {
        all: 'All',
        me: 'Assigned to me',
        unassigned: 'Unassigned',
      },
      sourceAutoHashCheck: 'Auto (hash check)',
      sourceAutoCsamHash: 'Auto (CSAM hash)',
      sourceAutoRekognition: 'Auto (Rekognition)',
      sourceUnknown: 'Unknown source',
      sourceManual: 'User Report',
      category: {
        csam: 'CSAM',
        illegal_content: 'Illegal content',
        violence: 'Violence',
        harassment: 'Harassment',
        spam: 'Spam',
        impersonation: 'Impersonation',
        other: 'Other',
      },
      col: {
        status: 'Status',
        category: 'Category',
        source: 'Source',
        target: 'Target',
        created: 'Created',
        assigned: 'Assigned',
      },
      prev: 'Previous',
      next: 'Next',
      pageOf: 'Page {{current}} of {{total}}',
    },
    tickets: {
      title: 'Support Tickets',
      subtitle: 'Review and respond to user support requests.',
      loadError: 'Failed to load tickets.',
      empty: 'No tickets match the current filters.',
      refresh: 'Refresh',
      filterStatus: 'Status',
      filterAssigned: 'Assignment',
      filterCategory: 'Category',
      statusAll: 'All',
      statusOpenInProgress: 'Open + In progress',
      status: {
        open: 'Open',
        in_progress: 'In progress',
        escalated: 'Escalated',
        resolved: 'Resolved',
        closed: 'Closed',
      },
      assigned: {
        all: 'All',
        me: 'Assigned to me',
        unassigned: 'Unassigned',
      },
      category: {
        account_access: 'Account access',
        billing: 'Billing',
        technical: 'Technical',
        privacy_security: 'Privacy & security',
        moderation_appeal: 'Moderation appeal',
        general: 'General',
      },
      col: {
        status: 'Status',
        category: 'Category',
        title: 'Title',
        submitter: 'Submitter',
        created: 'Created',
        assigned: 'Assigned',
      },
      prev: 'Previous',
      next: 'Next',
      pageOf: 'Page {{current}} of {{total}}',
      detail: {
        title: 'Ticket',
        loadError: 'Failed to load ticket.',
        notFound: 'Ticket not found.',
        backToList: 'Back to tickets',
        info: 'Ticket details',
        ticketId: 'Ticket ID',
        submitter: 'Submitter',
        category: 'Category',
        status: 'Status',
        created: 'Created',
        assignedTo: 'Assigned to',
        assign: 'Assign',
        unassign: 'Unassign',
        assignTo: 'Assign to',
        assignSelectPlaceholder: 'Select staff member…',
        escalate: 'Escalate to admin',
        resolve: 'Resolve',
        close: 'Close',
        reopen: 'Reopen',
        resolveTitle: 'Resolve ticket',
        resolveNotePlaceholder: 'Resolution note (required)…',
        confirmResolve: 'Confirm resolution',
        closeTitle: 'Close ticket',
        closeReasonPlaceholder: 'Reason for closing (required)…',
        confirmClose: 'Confirm close',
        addComment: 'Add comment',
        commentPlaceholder: 'Write a comment…',
        commentInternal: 'Internal (staff only)',
        commentPublic: 'Visible to submitter',
        postComment: 'Post',
        timeline: 'Timeline',
        staffLabel: 'Staff',
        submitterLabel: 'Submitter',
        internalNote: 'Internal note',
        statusChanged: 'Status updated',
        assignedToStaff: 'Assigned to {{name}}',
        unassignedEvent: 'Unassigned',
        escalatedEvent: 'Escalated to admin review',
        noComments: 'No activity yet.',
        noEvents: 'No events yet.',
        body: 'Description',
        attachments: 'Attachments',
      },
    },
    detail: {
      title: 'Report',
      loadError: 'Failed to load report.',
      notFound: 'Report not found.',
      backToList: 'Back to reports',
      info: 'Report details',
      type: 'Type',
      source: 'Source',
      category: 'Category',
      target: 'Target',
      targetIdentity: 'Target alias',
      reporter: 'Reporter alias',
      created: 'Created',
      assignedTo: 'Assigned to',
      assign: 'Assign',
      unassign: 'Unassign',
      assignTo: 'Assign to',
      assignSelectPlaceholder: 'Select moderator…',
      assignSuccess: 'Report assigned.',
      unassignSuccess: 'Report unassigned.',
      copiedAliasId: 'Alias ID copied to clipboard.',
      detectionMetadata: 'Detection metadata',
      scanEvidenceTitle: 'Scan preview (cleartext)',
      scanEvidenceLoadError: 'Could not load scan previews.',
      scanEvidenceEmpty:
        'No scan files are available. They may not have finished uploading yet, or evidence was already purged after this report was closed.',
      scanEvidencePartLabel: 'Part {{index}}',
      scanEvidenceRefresh: 'Refresh URLs',
      scanEvidenceExpiresHint:
        'These links expire after about {{minutes}} minutes. Refresh if previews stop loading.',
      scanEvidenceVideoUnsupported: 'Video playback is not supported in this browser.',
      reporterReason: 'Reporter notes',
      tabEvidence: 'Evidence',
      tabTargetHistory: 'Target History',
      tabReporterHistory: 'Reporter History',
      tabTimeline: 'Timeline',
      historyLoading: 'Loading history...',
      historyEmpty: 'No other reports found.',
      historyViewReport: 'View report',
      unknownAlias: 'Unknown alias',
      moderatorFallback: 'Moderator',
      evidence: 'Evidence',
      evidenceMessages: 'Message Evidence',
      contextWindowLabel: 'Reporter included {{count}} messages before and after the reported message.',
      evidenceOlderExpand: 'Older context ({{count}} messages)',
      evidenceNewerExpand: 'Newer context ({{count}} messages)',
      evidenceProfile: 'Profile Evidence',
      signatureVerified: 'Signature verified',
      signatureUnverified: 'Signature unverified',
      targetMessage: 'Reported message',
      contextMessage: 'Context',
      evidenceGif: 'GIF',
      evidenceSticker: 'Sticker',
      evidenceGifAlt: 'GIF or sticker preview',
      viewAttachment: 'View attachment',
      profileSnapshot: 'Profile snapshot (taken at report time)',
      resolution: 'Resolution',
      resolvedBy: 'Resolved by',
      closedLabel: 'Closed (invalid)',
      closedByLabel: 'Closed by',
      actions: 'Actions',
      reopen: 'Reopen for review',
      reopenTitle: 'Reopen report',
      reopenReasonPlaceholder: 'Reason for reopening (optional)...',
      confirmReopen: 'Confirm reopen',
      reopenSuccess: 'Report reopened.',
      escalate: 'Escalate',
      resolve: 'Resolve',
      close: 'Close (invalid)',
      resolveTitle: 'Resolve report',
      reasonPlaceholder: 'Reason (required)...',
      removeContent: 'Remove infringing content',
      warnUser: 'Warn the user',
      banAlias: 'Ban alias permanently',
      suspendDuration: 'Suspend alias',
      noSuspension: 'No suspension',
      confirmResolve: 'Confirm resolution',
      closeTitle: 'Close report as invalid',
      closeReasonPlaceholder: 'Reason for closing (required)...',
      confirmClose: 'Confirm close',
      addComment: 'Add comment',
      commentPlaceholder: 'Write a comment...',
      commentInternal: 'Internal (moderators only)',
      commentPublic: 'Public',
      postComment: 'Post',
      timeline: 'Timeline',
      noEvents: 'No events yet.',
      leReport: 'Report to Law Enforcement',
      leReportTitle: 'File Law Enforcement Report',
      leReportDescription:
        'This will record the report details for submission to law enforcement. Select a category and add any additional notes.',
      leReportCategory: 'Category',
      leReportNotes: 'Additional Notes',
      leReportNotesPlaceholder: 'Any additional context for the law enforcement report...',
      leReportSubmit: 'Submit Report',
      leReportContinue: 'Continue',
      leReportConfirmTitle: 'Confirm law enforcement submission',
      leReportConfirmDescription:
        'You are about to submit a {{category}} report to NCMEC CyberTipline. This action cannot be undone after the report is finished. Are you sure you want to proceed?',
      leReportConfirmSubmit: 'Submit to NCMEC',
      leReportSuccess: 'Law enforcement report filed successfully.',
      leReportSubmitFailed: 'NCMEC submission failed. You can retry from this page.',
      leReportRetry: 'Retry NCMEC submission',
      leReportFiled: 'LE Report Filed',
      leReportCategoryCsam: 'CSAM (Child Sexual Abuse Material)',
      leReportAlreadyFiled: 'An LE report has already been filed for this report.',
      ncmecReportId: 'NCMEC Report ID',
      ncmecSubmitFailed: 'NCMEC submission failed -- the report was recorded internally but could not be sent to NCMEC. Please retry or escalate.',
      eventType: {
        status_change: 'Status change',
        comment_internal: 'Internal comment',
        comment_public: 'Public comment',
        assignment_change: 'Assignment change',
        category_change: 'Category change',
        enforcement_action: 'Enforcement action',
        le_report_filed: 'LE report filed',
      },
      eventMeta: {
        statusTransition: '{{from}} → {{to}}',
        assignedTo: 'Assigned to {{user}}',
        unassigned: 'Unassigned',
        reassigned: 'Reassigned from {{from}} to {{to}}',
        categoryChanged: '{{from}} → {{to}}',
      },
    },
} as const;
